import { capitalize } from 'lodash-es';
import { useState } from 'react';
import { MultiSelect, Button, Layer } from '@carbon/react';
import styles from './add-models.module.scss';
import { Reset } from '@carbon/react/icons';
import { useScenarioForm } from '../contexts';
import { MineralType } from 'types/minerals';
import { ProductionStreamConfigData, StreamConfigKey } from '../types';
import { defaultModelConfigValues } from '../default-values';
import { getNextMaxMonthsOnline } from '../utils';
import { ModelType } from 'types/dca-scenarios-api/responses';

interface AddModelsProps {
  stream: MineralType;
  streamKey: StreamConfigKey;
  onSectionReset: () => void;
  configs: ProductionStreamConfigData[];
  disabled?: boolean;
}

export const AddModels = ({
  stream,
  streamKey,
  onSectionReset,
  disabled = false,
}: AddModelsProps) => {
  const [selectedModelTypes, setSelectedModelTypes] = useState<string[]>([]);

  const { form, defaultValues, updateFieldDefault } = useScenarioForm();

  const handleAddNewModels = () => {
    const newConfigs = selectedModelTypes.map((modelType) => {
      const configs = form.getFieldValue(streamKey) || [];
      const nextMaxMonthsOnline = getNextMaxMonthsOnline(configs, modelType);

      return {
        ...defaultModelConfigValues[modelType as ModelType],
        stream,
        maxMonthsOnline: nextMaxMonthsOnline,
      } as ProductionStreamConfigData;
    });

    const currentDefaults = defaultValues[streamKey];
    const newFormValue = [
      ...(form.state.values[streamKey] || []),
      ...newConfigs,
    ];

    form.setFieldValue(streamKey, newFormValue);
    updateFieldDefault(streamKey, [...(currentDefaults || []), ...newConfigs]);
    setSelectedModelTypes([]);
  };

  return (
    <div className={styles.container}>
      <Layer>
        <MultiSelect
          titleText="Model Type"
          size="md"
          id={`${stream}-model-type-multiselect`}
          label="Select Model Type(s)"
          items={['biased', 'unbiased', 'ramp_up', 'early_time']}
          itemToString={(item) => capitalize(item.replace('_', ' '))}
          selectedItems={selectedModelTypes}
          onChange={({ selectedItems }) =>
            setSelectedModelTypes(selectedItems ?? [])
          }
          selectionFeedback="fixed"
          sortItems={(items) => [...items]}
        />
      </Layer>
      <div className={styles.buttonContainer}>
        <Button
          size="md"
          kind="tertiary"
          onClick={handleAddNewModels}
          disabled={disabled}
        >
          Add New Model
        </Button>
        <Button
          size="md"
          kind="tertiary"
          className={styles.resetButton}
          onClick={onSectionReset}
          renderIcon={Reset}
          disabled={disabled}
        >
          Reset
        </Button>
      </div>
    </div>
  );
};
